<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFileInfo } from '@chatwoot/utils';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const fileDetails = computed(() => {
  return getFileInfo(attachment?.dataUrl || '');
});

const sameOriginUrl = url => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (parsedUrl.host === window.location.host) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    if (
      ['localhost:3000', '127.0.0.1:3000'].includes(parsedUrl.host) &&
      ['localhost:3000', '127.0.0.1:3000'].includes(window.location.host)
    ) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return url;
  } catch {
    return url;
  }
};

const attachmentUrl = computed(() => sameOriginUrl(attachment.dataUrl));

const attachmentThumbUrl = computed(() => sameOriginUrl(attachment.thumbUrl));

const displayFileName = computed(() => {
  const { base, type } = fileDetails.value;
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
});

const isPdf = computed(() => fileDetails.value.type === 'pdf');

const pdfPreviewUrl = computed(() => {
  if (attachmentThumbUrl.value) return attachmentThumbUrl.value;
  if (!attachmentUrl.value) return '';

  return `${attachmentUrl.value}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
});

const fileTypeLabel = computed(() => fileDetails.value.type?.toUpperCase());

const textColorClass = computed(() => {
  const colorMap = {
    '7z': 'dark:text-[#EDEEF0] text-[#2F265F]',
    csv: 'text-n-amber-12',
    doc: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    docx: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    json: 'text-n-slate-12',
    odt: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    pdf: 'text-n-slate-12',
    ppt: 'dark:text-[#FFE0C2] text-[#582D1D]',
    pptx: 'dark:text-[#FFE0C2] text-[#582D1D]',
    rar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    rtf: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    tar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    txt: 'text-n-slate-12',
    xls: 'text-n-teal-12',
    xlsx: 'text-n-teal-12',
    zip: 'dark:text-[#EDEEF0] text-[#2F265F]',
  };

  return colorMap[fileDetails.value.type] || 'text-n-slate-12';
});
</script>

<template>
  <div
    v-if="isPdf"
    class="block w-64 overflow-hidden transition-colors border rounded-xl bg-n-alpha-white border-n-container hover:bg-n-alpha-1"
  >
    <div
      class="relative h-36 overflow-hidden bg-n-slate-2 border-b border-n-container"
    >
      <img
        v-if="attachmentThumbUrl"
        :src="pdfPreviewUrl"
        :alt="fileDetails.name"
        class="object-cover object-top w-full h-full"
      />
      <iframe
        v-else-if="pdfPreviewUrl"
        :src="pdfPreviewUrl"
        :title="fileDetails.name"
        class="w-full h-44 pointer-events-none -mt-2 bg-white"
      />
      <div v-else class="grid h-full place-content-center">
        <div
          class="flex flex-col items-center gap-2 text-xs font-medium text-n-ruby-11"
        >
          <FileIcon file-type="pdf" class="size-12" />
          <span>{{ fileTypeLabel }}</span>
        </div>
      </div>
      <div
        class="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-n-slate-2 to-transparent"
      />
      <a
        v-if="attachmentUrl"
        :href="attachmentUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
        class="absolute inset-0 cursor-pointer"
        :aria-label="fileDetails.name"
      />
    </div>
    <div class="flex items-center gap-2 px-3 py-2">
      <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
      <span
        class="flex-1 min-w-0 text-sm truncate"
        :title="fileDetails.name"
        :class="textColorClass"
      >
        {{ fileDetails.name || displayFileName }}
      </span>
      <a
        v-tooltip="t('CONVERSATION.DOWNLOAD')"
        :href="attachmentUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
        class="flex-shrink-0 text-n-slate-11 hover:text-n-slate-12 transition-colors"
      >
        <Icon icon="i-lucide-download" />
      </a>
    </div>
  </div>
  <div
    v-else
    class="h-9 bg-n-alpha-white gap-2 overflow-hidden items-center flex px-2 rounded-lg border border-n-container"
  >
    <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
    <span
      class="flex-1 min-w-0 text-sm max-w-36"
      :title="fileDetails.name"
      :class="textColorClass"
    >
      {{ displayFileName }}
    </span>
    <a
      v-tooltip="t('CONVERSATION.DOWNLOAD')"
      class="flex-shrink-0 size-9 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
      :href="attachmentUrl"
      rel="noreferrer noopener nofollow"
      target="_blank"
    >
      <Icon icon="i-lucide-download" />
    </a>
  </div>
</template>
